import axios from 'axios';
import { generatePath, matchPath } from 'react-router';

import { call, fork, put, takeEvery, takeLatest, select, take, debounce, delay, race, all } from 'redux-saga/effects';

import { fetchDataForCurrentUser } from 'actions/userActions';
import { checkRights } from 'components/common/RightsUtils';
import { restClient } from 'config/store/configureStore';
import * as types from 'constants/ActionTypes';
import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { DEALER_ALREADY_USED } from 'constants/ErrorConstants';
import { DEFAULT } from 'constants/ExtensionConstants';
import { DEBOUNCE_TIME } from 'constants/Global';
import { RIGHTS_ACCESS, EXTENSIONS, FEATURES, FLEETBACK_PACK, ODITOO_PACK } from 'constants/RightsConstants';
import { PATH } from 'constants/RoutesConstants';
import { SELF_SERVICE_ORIGIN } from 'constants/SelfServiceConstants';
import { DEALER_STATUS } from 'constants/StatusConstants';

import { isApplicationReady } from 'modules/app/actions';
import { APPLICATION_LOADED, APPLICATION_IS_READY } from 'modules/app/actionTypes';
import { fetchPage } from 'modules/app/sagas/resolveAll';
import { applicationIsReady, applicationIsStarted, locationHasChanged } from 'modules/app/selectors';
import { simpleRequestsLoader } from 'modules/app/utils';
import { resetDocuments } from 'modules/config/actions/general';
import * as dealerActions from 'modules/dealers/actions';
import * as actionTypes from 'modules/dealers/actionTypes';
import * as dashboardSelectors from 'modules/dealers/selectors/dashboard';
import * as modalSelector from 'modules/dealers/selectors/modalSelect';
import * as paginatedDealerSelectSelector from 'modules/dealers/selectors/select';
import { fetchGroupsSelect, selectGroup } from 'modules/groups/actions';
import { getSelectedGroup, getSelectedGroupId } from 'modules/groups/selectors';
import { getDealerAndGroupAssignedQuestionFormModelIds } from 'modules/groups/selectors/questionFormModels';
import { getLocale } from 'modules/i18n/selectors/data';
import { fetchAvailableQuestions, fetchDefaultQuestionModel } from 'modules/questionModels/actions';
import {
  FETCH_QUESTION_MODELS_FAILURE,
  FETCH_QUESTION_MODELS_SUCCESS,
  FETCH_DEFAULT_QUESTION_MODEL_FAILURE,
  FETCH_DEFAULT_QUESTION_MODEL_SUCCESS
} from 'modules/questionModels/actionTypes';

import { getDefaultQuestionId } from 'modules/questionModels/selectors';
import { getParams as getQuestionsParams } from 'modules/questionModels/selectors/ui/configSelect';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { navigate } from 'modules/router/navigate';
import { getLocation, isFirstRendering as isFirstRenderingSelector } from 'modules/router/selectors';
import { getOnSiteLink } from 'modules/selfServices/actions';
import { createNotification } from 'modules/toast-notifications/actions';

import {
  getCurrentUserId,
  getUserAccessMatrix,
  getCurrentUserDealerIds,
  isCurrentUserIsAdminOrGroupManager
} from 'modules/users/selectors/currentUser';
import {
  getDealerById,
  getSelectedDealer
} from 'selectors/dealerSelectors';

import { extractExtensionFromAction, extractAutoSelectFromAction, hasSelfServiceOrigin } from 'services/DealerUtils';
import { getCurrentRoute } from 'services/RouterUtils';

import { CHECK_INIT_DEALER_PAGES, OVERRIDE_PROJECTION_FOR_PAGES, SKIP_INIT_DEALER_PAGES } from './constants';

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [types.CREATE_DEALER_REQUEST]: {
    notifications: {
      success: {
        id: 'page.config.admin.dealer.create.notification.create.success',
        defaultMessage: 'Dealer created'
      },
      failure: {
        id: 'page.config.admin.dealer.create.notification.create.failure',
        defaultMessage: "Can't create the dealer"
      }
    }
  },
  [actionTypes.TRIGGER_KEYLOOP_REFRESH]: {
    notifications: {
      success: {
        id: 'page.config.dealer_general.integration.keyloop.triggerRefresh.notification.success',
        defaultMessage: 'Keyloop refresh successfully'
      },
      failure: {
        id: 'page.config.dealer_general.integration.keyloop.triggerRefresh.notification.failure',
        defaultMessage: 'Failed to refresh Keyloop'
      }
    }
  }
};

const defaultParams = {
  fields: 'id,name,groupId,features',
  sort: 'name,asc',
  status: DEALER_STATUS.ACTIVE
};

/**
 * @param {import('modules/global/generic.types').LocationChangeAction} action
 */
function* initializeDealerDetails({ payload }) {
  const match = matchPath(PATH.DEALERS_DETAILS.path, payload.location.pathname);
  const hasChanged = yield select(locationHasChanged);

  if (hasChanged && match) {
    const isFirstRendering = yield select(isFirstRenderingSelector);
    if (isFirstRendering) {
      yield take(APPLICATION_IS_READY);
    }

    // Load dealer and group only for edition, not creation
    yield put(dealerActions.dealerNameCheckSuccess());
    if (match.params.dealerId !== NEW_ENTITY_ID) {
      const { dealerId } = match.params;
      yield put(dealerActions.selectDealer(dealerId, { projection: 'config' }));
    }
  }
}

/**
 * @param {import('modules/dealers/types').FetchDealersAction} action
 */
function* fetchDealers({ payload }) {
  const { request } = payload;
  try {
    const data = yield* fetchPage(request);
    yield put(dealerActions.fetchDealersSuccess(data));
  } catch (error) {
    yield put(dealerActions.fetchDealersFailure(error));
  }
}

/**
 * @param {import('modules/dealers/types').FetchDealersByReducerAction} action
 */
function* fetchDealersByReducer({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(dealerActions.fetchDealersSuccess(data));
  } catch (error) {
    yield put(dealerActions.fetchDealersFailure(error));
  }
}

/**
 * @param {import('modules/dealers/types').FetchDealersSelectAction} action
 */
function* fetchDealersSelect(action) {
  try {
    const sourceMeta = action.meta ?? {};
    let meta = { extension: DEFAULT };
    const { request } = action.payload;
    const data = yield* fetchPage(request);
    const extension = extractExtensionFromAction(action);
    const autoSelect = extractAutoSelectFromAction(action);

    if (extension) {
      meta = { extension };
    }

    if (autoSelect !== undefined) {
      meta = { ...meta, autoSelect };
    }

    yield put(dealerActions.fetchDealersSelectSuccess(data, { ...sourceMeta, ...meta }));
  } catch (e) {
    yield put(dealerActions.fetchDealersSelectFailure(e));
  }
}

/**
 * @param {import('modules/dealers/types').FetchDealerAction} action
 */
function* fetchDealer({ payload, meta }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(dealerActions.fetchDealerSuccess(data));

    const shouldSelectGroup = meta.selectGroupId && data.group.id;
    if (shouldSelectGroup) {
      yield put(selectGroup(data.group.id));
    }
  } catch (e) {
    yield put(dealerActions.fetchDealerFailure(e));
  }
}

/**
 * @param {import('modules/dealers/types').SelectDealerAction} action
 */
function* fetchSelectedDealer({ payload, meta }) {
  if (payload.dealerId) {
    yield put(dealerActions.fetchDealer(payload.dealerId, { projection: 'details', ...(payload.params ?? {}) }, meta));
    const { payload: dealer } = yield take(types.FETCH_DEALER_SUCCESS);

    const groupId = dealer.group?.id ?? dealer.groupId;
    const group = yield select(getSelectedGroup);

    if (groupId !== group?.id) {
      yield put(selectGroup(groupId));
    }
  }
}

function* multipleToast({ meta }) {
  for (const notification of meta.notifications) {
    yield put(createNotification({ type: 'success', message: notification }));
  }
}

const channel = ({ type, meta }) => type === types.UPDATE_DEALER_SUCCESS && meta?.notifications?.length;

/**
 * @param {import('modules/dealers/types').UpdateDealerAction} action
 */
function* updateDealer({ payload, meta }) {
  const { request } = payload;
  try {
    const response = yield call(restClient.request, request);
    const { data: dealer } = response;
    yield put(dealerActions.updateDealerSuccess(dealer, meta));
    yield put(resetDocuments());
    yield put(
      createNotification({
        type: 'success',
        message: 'page.config.admin.dealer.update.notification.save.success'
      })
    );
  } catch (error) {
    yield put(dealerActions.updateDealerFailure(error, meta));
    const { response } = error;

    yield put(
      createNotification({
        type: 'error',
        message: `page.config.admin.dealer.update.notification.save.${
          response?.data?.errorType === DEALER_ALREADY_USED ? 'merchantAlreadyConfigured' : 'failure'
        }`
      })
    );
  }
}

/**
 * @param {import('modules/dealers/types').UpdateDealerSuccessAction} action
 */
function* afterUpdateDealer({ payload, meta }) {
  /** @type {Array<string>} */
  const dealerIds = yield select(getCurrentUserDealerIds);
  const haveDealer = dealerIds?.includes(payload.id) ?? false;

  if (typeof dealerIds === 'undefined' || haveDealer) {
    const userId = yield select(getCurrentUserId);
    yield put(fetchDataForCurrentUser(userId, meta));
  }
}

/**
 * @param {import('modules/dealers/types').DealerNameCheckAction} action
 */
function* checkDealerNameWorker({ payload, meta }) {
  const { request } = payload;
  let success = false;

  try {
    /** @type {import('axios').AxiosResponse<boolean>} */
    const response = yield call(restClient.request, request);
    if (response.status !== 204) {
      success = true;
    }
  } catch (error) {
    if (error.response.status === 404) {
      success = true;
    }
  }
  yield put(
    success
      ? dealerActions.dealerNameCheckSuccess(undefined, meta)
      : dealerActions.dealerNameCheckFailure(undefined, meta)
  );
}

function* afterOpenModal() {
  const locale = yield select(getLocale);

  const groupsParams = {
    reducer: ['id,none', 'name,none'],
    sort: 'name,asc',
    lang: locale,
  };
  yield put(fetchGroupsSelect(groupsParams));
  yield call(refreshModalResults);
}

/**
 * @param {import('modules/dealers/types').GetDealersModalFetchAction} action
 */
function* getDealerModal({ payload, meta }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(dealerActions.getDealersModalSuccess(data, meta));
  } catch (error) {
    yield put(dealerActions.getDealersModalFailure(error, meta));
  }
}

function* refreshModalResults() {
  const params = yield select(modalSelector.getParams);
  yield put(dealerActions.getDealersModal({ ...params, page: 0 }));
}

/**
 * @param {Array<import("modules/global/typing/enums/extensions.enum").ExtensionEnum>} extensions
 * @param {Array<import("modules/global/typing/enums/features.enum").FeaturesEnum>} features
 * @param {Array<string>} groupIds
 */
function* getDealersCollection(extensions, features, groupIds) {
  const locale = yield select(getLocale);
  const params = {
    ...defaultParams,
    size: 5,
    lang: locale,
    extensions,
    features,
    groupIds,
    status: DEALER_STATUS.ACTIVE,
    // Need to have config.pack to improve the rights control
    fields: 'id,name,groupId,extensions,features,brandIds,status,config,config.pack'
  };
  yield put(dealerActions.fetchDealersSelect(null, params));
  const action = yield take(types.FETCH_DEALERS_SELECT_SUCCESS);
  return action.payload.content;
}

/** @return {(dealer:import('./_types/types.ts').Dealer, skipStatusCheck?: boolean) => boolean} */
const isAvailableDealer =
  (userRightsMatrix, pathname) =>
    (dealer, skipStatusCheck = false) => {
      const route = getCurrentRoute(pathname);

      const { extensions = [], features = [], status, config = {} } = dealer;
      const { pack } = config;

      const oditooPack = pack?.oditooPack ? [pack.oditooPack] : [];
      const fleetBackPack = pack?.fleetBackPack ? [pack.fleetBackPack] : [];

      const dealerRights = {
        ...userRightsMatrix,
        [FEATURES]: features,
        [EXTENSIONS]: extensions,
        [ODITOO_PACK]: oditooPack,
        [FLEETBACK_PACK]: fleetBackPack
      };

      const haveAvailableStatus = skipStatusCheck || status === DEALER_STATUS.ACTIVE;

      return haveAvailableStatus && checkRights(route, dealerRights);
    };

/**
 * Return the page access rights
 * @param {string} pathname
 * @return {Object}
 */
const getRights = pathname => {
  const route = getCurrentRoute(pathname);
  return RIGHTS_ACCESS[route];
};

/**
 * Return the collection of available dealer ids, for a specific app path
 * @description This function check the accessibility rights defined for the given path
 * @param {string} pathname
 * @return {Array<string>} - Collection of dealer Ids
 */
export function* getAvailableDealerIds(pathname) {
  const useRightMatrix = yield select(getUserAccessMatrix);
  const groupId = yield select(getSelectedGroupId);
  const rights = getRights(pathname);

  // Try to load dealer in the same group
  let allDealers = yield call(
    getDealersCollection,
    rights?.EXTENSIONS,
    rights?.FEATURES,
    groupId ? [groupId] : undefined
  );

  // Try to load a dealer without group
  if (allDealers.length === 0 && groupId) {
    allDealers = yield call(getDealersCollection, rights?.EXTENSIONS, rights?.FEATURES);
  }

  return allDealers
    .filter(isAvailableDealer(useRightMatrix, pathname))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ id: dealerId }) => dealerId);
}

/**
 * Restore the dealer a creation page was opened for (carried in the navigation state, BMWVID-29089).
 * The dealer is fetched then checked against the CURRENT user's rights — a stale navigation state
 * never grants access to a dealer the user cannot use.
 * @param {import('modules/global/typing/common/common').MayBe<string>} dealerId
 * @param {string} pathname
 * @param {string} path
 * @param {boolean} skipStatusCheck
 * @returns {boolean} true when the dealer was restored and selected
 */
function* restoreDealerFromLocationState(dealerId, pathname, path, skipStatusCheck) {
  if (!dealerId) {
    return false;
  }

  yield put(dealerActions.selectDealer(dealerId, { projection: OVERRIDE_PROJECTION_FOR_PAGES[path] }));
  const { error } = yield take([types.FETCH_DEALER_SUCCESS, types.FETCH_DEALER_FAILURE]);
  if (error) {
    return false;
  }

  const dealer = yield select(getSelectedDealer);
  const useRightMatrix = yield select(getUserAccessMatrix);
  return Boolean(dealer && isAvailableDealer(useRightMatrix, pathname)(dealer, skipStatusCheck));
}

/**
 * Check rights, and auto select an available dealer
 * @description If the selected dealer, is not available in the current view, we select another one
 * @param {string} pathname
 * @param {string} path
 */
function* autoSelectDealer(pathname, path) {
  const skipStatusCheck = [PATH.DEALERS_DETAILS.path, PATH.DEALERS.path].includes(path);

  const currentDealer = yield select(getSelectedDealer);
  const useRightMatrix = yield select(getUserAccessMatrix);
  const currentDealerIsAvailable = currentDealer && isAvailableDealer(useRightMatrix, pathname)(currentDealer, skipStatusCheck);

  if (!currentDealerIsAvailable) {
    // Creation pages carry the dealer they were opened for in the navigation state (BMWVID-29089),
    // so a hard refresh restores that dealer instead of falling back to the first available one.
    const { state } = yield select(getLocation);
    const restored = yield call(restoreDealerFromLocationState, state?.dealerId, pathname, path, skipStatusCheck);
    if (restored) {
      return;
    }

    const availableDealers = yield call(getAvailableDealerIds, pathname);
    const [newDealerId] = availableDealers;
    yield put(dealerActions.selectDealer(newDealerId ?? null, { projection: OVERRIDE_PROJECTION_FOR_PAGES[path] }));
    if (newDealerId) {
      // Wait the end of the call before finishing the process
      yield take([types.FETCH_DEALER_SUCCESS, types.FETCH_DEALER_FAILURE]);
    }
  }
}

/**
 * Check that the selected dealer is available in the current interface
 * @param {string} pathname
 * @param {boolean} [autoSelect=false]
 * @param {Array} [additionalSkipPages=[]]
 */
function* verifySelectedDealer(pathname, autoSelect = false, additionalSkipPages = []) {
  /** @type {import('./types').Dealer} */
  const currentDealer = yield select(getSelectedDealer);
  let pages = CHECK_INIT_DEALER_PAGES;
  const skipPages = [...SKIP_INIT_DEALER_PAGES, ...additionalSkipPages];

  if (autoSelect) {
    skipPages.push(PATH.STATS_SALES, PATH.STATS_AFTER_SALES, PATH.STATS_PARTS, PATH.SELF_SERVICE_STATS_DASHBOARD);
  }

  const isDealerCreation = matchPath(PATH.DEALERS_DETAILS.path, pathname)?.params?.dealerId === NEW_ENTITY_ID;
  const shouldSkip = !isDealerCreation && skipPages.some(page => matchPath(page.path, pathname));

  // Only if the current user is an Admin or a group manager, we should control the interventions dashboards
  const isAdminOrGroupManager = yield select(isCurrentUserIsAdminOrGroupManager);
  if (isAdminOrGroupManager) {
    pages = [...pages, PATH.AFTERSALES, PATH.SALES, PATH.PARTS, PATH.ERROR_404, PATH.ERROR_GLOBAL];
  }

  const { path: mandatoryPath } =
    pages.find(page => matchPath(page.path, pathname) || (page.simplePath && matchPath(page.simplePath, pathname))) ??
    {};

  const mustControlSelectedDealer = !shouldSkip && (autoSelect || currentDealer || Boolean(mandatoryPath));
  // The selected dealer must be controlled, for the defined pages and if the admin app is started
  if (mustControlSelectedDealer) {
    yield call(autoSelectDealer, pathname, mandatoryPath);
  }
}

/**
 * Callback call when a user is successfully connected
 */
function* afterAuthenticationSuccess() {
  /** @type {import('react-router').Location} */
  const location = yield select(getLocation);
  const autoSelect = yield select(isCurrentUserIsAdminOrGroupManager);

  yield call(verifySelectedDealer, location.pathname, autoSelect);
  yield put(isApplicationReady());
}

/**
 * Check the selected dealer when the user navigate between the interfaces
 * @param {import('modules/global/generic.types').LocationChangeAction} action
 */
function* locationChangeWorker({ payload }) {
  const { pathname } = payload.location;
  const isStarted = yield select(applicationIsStarted);

  if (isStarted) {
    yield call(verifySelectedDealer, pathname, false, [PATH.DEALERS]);
  }
}

export function* selectFirstAvailableDealer(pathname) {
  const [newDealerId] = yield call(getAvailableDealerIds, pathname);
  yield put(dealerActions.selectDealer(newDealerId));
}

function* fetchSelfServiceConfigQuestions(dealerId) {
  const meta = { source: 'config' };

  const questionParams = yield select(getQuestionsParams);
  const questionIds = yield select(getDealerAndGroupAssignedQuestionFormModelIds);

  // In first, we need to fetch the default question
  const defaultQuestionId = yield select(getDefaultQuestionId);
  if (!defaultQuestionId) {
    yield put(fetchDefaultQuestionModel());
    yield take([FETCH_DEFAULT_QUESTION_MODEL_SUCCESS, FETCH_DEFAULT_QUESTION_MODEL_FAILURE]);
  }

  // Fetch preselected questions
  const shouldFetchSelectedQuestions = questionIds.length > 0;
  if (shouldFetchSelectedQuestions) {
    yield put(fetchAvailableQuestions({ ids: questionIds, size: questionIds.length, dealerId }, meta));
    yield take([FETCH_QUESTION_MODELS_SUCCESS, FETCH_QUESTION_MODELS_FAILURE]);
  }

  yield put(
    fetchAvailableQuestions({ ...questionParams, page: 0 }, { ...meta, skipReset: shouldFetchSelectedQuestions })
  );
}

/** @param {string} dealerId */
export function* getSelfServiceConfig(dealerId) {
  yield call(fetchSelfServiceConfigQuestions, dealerId);

  const dealer = yield select(state => getDealerById(state, dealerId));
  if (dealer && !dealer.config.selfService?.publicLink) {
    const availableTypes = dealer.config.selfService?.availableTypes;
    if (hasSelfServiceOrigin(availableTypes, SELF_SERVICE_ORIGIN.ON_SITE)) {
      yield put(getOnSiteLink(dealerId, { isPublic: true }, { target: 'public' }));
    }
  }
}

function* fetchDealerBuffered() {
  const pattern = actionTypes.LOAD_DEALER;
  while (true) {
    const dealerIds = [];
    let action = yield take(pattern);

    while (true) {
      const { payload: dealerId, meta } = action;
      dealerIds.push(dealerId);

      const { debounced, latestAction } = yield race({
        debounced: delay(100),
        latestAction: take(pattern)
      });

      if (debounced) {
        yield put(
          dealerActions.fetchDealersSelect(
            undefined,
            { ids: dealerIds, size: dealerIds.length },
            undefined,
            undefined,
            meta
          )
        );
        break;
      }

      action = latestAction;
    }
  }
}

/**
 * @param {import('modules/dealers/types').UploadUsersDealerAction} action
 */
function* uploadUsersDealer({ payload, meta }) {
  try {
    const { request } = payload;
    if (meta.file?.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      throw new Error('Only xlsx file is accepted to perform the upload !');
    }
    const { data } = yield call(restClient.request, request);
    yield call(axios.put, data.path, meta.file); // we use axios directly to avoid having the bearer token as we do not call Fleetback domain
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.config.admin.dealer.update.usersImport.success',
          defaultMessage: 'The file has been successfully uploaded'
        }
      })
    );
  } catch {
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.admin.dealer.update.usersImport.failure',
          defaultMessage: 'An error has occurred while uploading the file'
        }
      })
    );
  }
}

/**
 * @param {import('modules/dealers/types').InitializeDealerSelectorAction} action
 */
function* initializePaginatedDealerSelect({ payload }) {
  const params = yield select(state => paginatedDealerSelectSelector.getParams(state, payload.token));
  yield put(dealerActions.getPaginatedDealers(payload.token, params));
}

/**
 * @param {import('modules/dealers/types').GetPaginatedDealersAction} action
 */
function* fetchPaginatedDealerSelect({ payload, meta }) {
  const { request, token } = payload;
  try {
    const data = yield* fetchPage(request);
    yield put(dealerActions.getPaginatedDealersSuccess(token, data, meta));
  } catch (error) {
    yield put(dealerActions.getPaginatedDealersFailure(token, error, meta));
  }
}

/**
 * @param {import('modules/global/generic.types').LocationChangeAction} action
 */
function* onLocationChange({ payload }) {
  const isMatching = matchPath(PATH.DEALERS.path, payload.location.pathname);
  if (isMatching) {
    const isFirstRendering = yield select(isFirstRenderingSelector);
    if (isFirstRendering) {
      yield take(APPLICATION_LOADED);
      const isAdminOrGroupManager = yield select(isCurrentUserIsAdminOrGroupManager);
      const toWait = isAdminOrGroupManager ? types.FETCH_DEALER_SUCCESS : APPLICATION_IS_READY;
      yield take(toWait);
    }

    const params = yield select(dashboardSelectors.getParams);
    yield put(dealerActions.fetchDealers(undefined, { ...params, page: 0 }));
    yield put(dealerActions.dashboardIsReady());
  }
}

function* onDealerChange() {
  const location = yield select(getLocation);
  const isMatching = matchPath(PATH.DEALERS.path, location.pathname);
  const isReady = yield select(applicationIsReady);

  if (isMatching && isReady) {
    const params = yield select(dashboardSelectors.getParams);
    yield put(dealerActions.fetchDealers(undefined, { ...params, page: 0 }));
  }
}

function* getPredefinedMessages({ payload, meta }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(dealerActions.getPredefinedMessagesSuccess(data, meta));
  } catch (error) {
    yield put(dealerActions.getPredefinedMessagesFailure(error, meta));
  }
}

function* fetchDealersWatcher() {
  yield takeEvery(types.FETCH_DEALERS_REQUEST, fetchDealers);
}

function* fetchDealersByReducerWatcher() {
  yield takeLatest(types.FETCH_DEALERS_BY_REDUCER_REQUEST, fetchDealersByReducer);
}

function* fetchDealersSelectWatcher() {
  yield takeEvery(types.FETCH_DEALERS_SELECT_REQUEST, fetchDealersSelect);
}

function* fetchDealerWatcher() {
  yield takeLatest(types.FETCH_DEALER_REQUEST, fetchDealer);
}

function* selectDealerWatcher() {
  yield takeLatest(types.SELECT_DEALER, fetchSelectedDealer);
}

function* updateDealerWatcher() {
  yield takeLatest(types.UPDATE_DEALER_REQUEST, updateDealer);
  yield takeLatest(types.UPDATE_DEALER_SUCCESS, afterUpdateDealer);
}

function* updateDealerMultipleToastWatcher() {
  yield takeLatest(channel, multipleToast);
}

function* checkDealerNameWatcher() {
  yield takeEvery(types.DEALER_NAME_CHECK_REQUEST, checkDealerNameWorker);
}

function* dealersDashboardWorker() {
  yield takeEvery(LOCATION_CHANGE, onLocationChange);
  yield takeEvery(types.SELECT_DEALER, onDealerChange);
}

// Dealers details page
function* dealersConfigWorker() {
  yield takeEvery(actionTypes.PREDEFINED_MESSAGES_FETCH, getPredefinedMessages);
}

/**
 * @param {import('modules/dealers/types').CreateDealerSuccessAction} action
 */
function* afterCreateDealerSuccess({ payload: dealer }) {
  const detailsPath = generatePath(PATH.DEALERS_DETAILS.path, { dealerId: dealer.id });
  yield call(navigate, detailsPath);
}

function* controlDealerModalWatcher() {
  yield all(simpleRequestsLoader(simpleRequests));
  yield takeEvery(types.CREATE_DEALER_SUCCESS, afterCreateDealerSuccess);

  yield fork(fetchDealerBuffered);
  yield takeEvery(LOCATION_CHANGE, initializeDealerDetails);
  yield takeEvery(LOCATION_CHANGE, locationChangeWorker);
  yield takeEvery(APPLICATION_LOADED, afterAuthenticationSuccess);

  yield takeEvery([actionTypes.OPEN_SELECT_DEALER_MODAL], afterOpenModal);
  yield takeEvery(
    [actionTypes.REMOVE_MODAL_DEALERS_FILTER, actionTypes.CLEAR_MODAL_DEALERS_FILTER],
    refreshModalResults
  );
  yield debounce(DEBOUNCE_TIME, actionTypes.SET_MODAL_DEALERS_FILTER, refreshModalResults);
  yield takeEvery(actionTypes.GET_MODAL_DEALERS, getDealerModal);
  yield takeEvery(actionTypes.GET_PAGINATED_DEALERS_FETCH, fetchPaginatedDealerSelect);
  yield debounce(150, actionTypes.SET_PAGINATED_DEALERS_FILTERS, initializePaginatedDealerSelect);

  yield takeLatest(actionTypes.UPLOAD_USERS_DEALER, uploadUsersDealer);
}

const dealersSagas = [
  fork(dealersConfigWorker),
  fork(fetchDealerWatcher),
  fork(fetchDealersWatcher),
  fork(selectDealerWatcher),
  fork(updateDealerWatcher),
  fork(checkDealerNameWatcher),
  fork(fetchDealersSelectWatcher),
  fork(controlDealerModalWatcher),
  fork(fetchDealersByReducerWatcher),
  fork(dealersDashboardWorker),
  fork(updateDealerMultipleToastWatcher)
];

export default dealersSagas;
